import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import Joi from 'joi';
import _ from 'lodash';

import { useGetRolesQuery, useSignUpMutation } from 'api/main';
import { tunnelSelectors, userSelectors } from 'reducers';
import { isStatusOk, validateSchema } from 'utils/helpers';
import { useGetReferralParameters, useTunnelChangeHandlers } from 'utils/hooks';
import { initUserPayload } from 'schemas/tunnel';
import {
    userAvailabilityStatus,
    EMAIL_VALIDATION_OPTIONS,
    InternalErrors,
    PASS_REGEX,
    stepStatus,
    NAME_REGEX,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import BasicInfoForm from './BasicInfoForm';
import PasswordForm from './PasswordForm';
import ConditionsForm from '../contact-info/forms/ConditionsForm';
import { TunnelBox } from 'containers/tunnel/common';
import { ErrorMessage, SubmitButton } from 'components';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    field: { width: '100%', maxWidth: theme.constants.tunnelInputWidth },
    indentedField: { paddingLeft: 20 },
}));

const PPForm = props => {
    const { userStep, roleName, tunnelStep, handleNextStep } = props;
    const classes = useStyles();

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const { subcategory, product } = useGetReferralParameters();
    const { data: roles = [] } = useGetRolesQuery();
    const [signUp, { isLoading, isSuccess, isError, error, reset }] = useSignUpMutation({
        fixedCacheKey: 'register',
    });

    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const tunnelErrors = useSelector(tunnelSelectors.getTunnelErrors);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const currentInvestmentFund = useSelector(tunnelSelectors.getCurrentInvestmentFund);

    const {
        label,
        email: queryEmail,
        externalDistributorEmail,
        firstName: queryFirstName,
        lastName: queryLastName,
        title: queryCivility,
    } = useGetReferralParameters();
    const defaultForm = useMemo(
        () =>
            initUserPayload(
                isCreatingSubscription ? null : internalUser,
                tunnelSubscription,
                queryEmail,
                queryFirstName,
                queryLastName,
                queryCivility
            ),
        [
            isCreatingSubscription,
            internalUser,
            tunnelSubscription,
            queryEmail,
            queryFirstName,
            queryLastName,
            queryCivility,
        ]
    );
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    useEffect(() => setForm(defaultForm), [defaultForm]);

    const [emailExists, setEmailExists] = useState(false);
    const roleId = useMemo(() => roles.find(r => r.name === roleName)?.id, [roles, roleName]);
    const fundId = useMemo(
        () => product || currentInvestmentFund?.id,
        [product, currentInvestmentFund]
    );
    const isAccountNotConfirmed = useMemo(
        () => internalUser?.availabilityStatus === userAvailabilityStatus.PENDING_PASSWORD_CHANGE,
        [internalUser]
    );
    const isResubmit = useMemo(
        () => userStep?.successfulAttemptsNumber > 0 || userStep?.status === stepStatus.INCOMPLETE,
        [userStep]
    );
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);

    const schema = useMemo(
        () => ({
            firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_PRENOM,
                'string.min': texts.FIRST_NAME_LENGTH,
                'string.max': texts.FIRST_NAME_LENGTH,
                'string.pattern.base': texts.FIRST_NAME_PATTERN,
            }),
            lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
                'string.min': texts.LAST_NAME_LENGTH,
                'string.max': texts.LAST_NAME_LENGTH,
                'string.pattern.base': texts.LAST_NAME_PATTERN,
            }),
            birthName: Joi.string().trim().max(50).pattern(NAME_REGEX).allow('').messages({
                'string.max': texts.VEUILLEZ_SAISIR_VOTRE_NOM,
                'string.pattern.base': texts.BIRTHNAME_PATTERN,
            }),
            email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
                'string.empty': texts.SAISIR_VOTRE_ADRESSE_EMAIL,
                'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
            }),
            password: isCreatingSubscription
                ? Joi.any()
                : Joi.string().pattern(PASS_REGEX).required().messages({
                      'string.empty': texts.EMPTY_NEW_PASSWORD,
                      'string.pattern.base': texts.LE_MOT_DE_PASSE_DOIT_CONTENIR,
                  }),
            confirmPassword: Joi.when('password', {
                is: '',
                then: Joi.any(),
                otherwise: Joi.ref('password'),
            }).messages({ 'any.only': texts.PASSWORDS_MUST_MATCH }),
            conditions: Joi.object({
                termsAgreement: Joi.boolean().valid(true),
                acceptedEmails: Joi.boolean().valid(true),
            }),
        }),
        [isCreatingSubscription]
    );

    const resubmitSchema = useMemo(
        () => ({
            firstName: schema.firstName,
            lastName: schema.lastName,
            birthName: schema.birthName,
            conditions: schema.conditions,
        }),
        [schema]
    );

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(isResubmit ? resubmitSchema : schema, form);
        setErrors(newErrors);

        return valid;
    }, [form, isResubmit, schema, resubmitSchema]);

    const handleChange = key => e => {
        const { value } = e.target;
        setForm(oldForm => ({ ...oldForm, [key]: value }));
        setEmailExists(false); // remove the error message on any field change
    };
    const handleConditionsChange = useCallback(e => {
        const { name, checked } = e.target;
        setForm(prevValue => ({
            ...prevValue,
            conditions: { ...prevValue.conditions, [name]: checked },
        }));
    }, []);

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return !_.isEqual(defaultForm, form);
    }, [form, defaultForm]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);
    const submitButtonText = useMemo(() => {
        if (changesMade) {
            return isResubmit || isAccountNotConfirmed ? texts.SAVE_AND_CONTINUE : texts.CREATE;
        } else {
            return isResubmit || isAccountNotConfirmed ? texts.SUIVANT : texts.CREATE;
        }
    }, [changesMade, isResubmit, isAccountNotConfirmed]);

    const handleSubmit = useCallback(
        () => () => {
            if (!formIsValid()) return;
            if (isResubmit) {
                const { password, confirmPassword, ...rest } = form;
                signUp({
                    data: {
                        ...rest,
                        roleName,
                        stepId: tunnelStep.step.id,
                        roleId: roleId + '',
                        fundId: fundId + '',
                        fundName: currentInvestmentFund?.name,
                        label,
                    },
                    resubmit: true,
                });
            } else {
                const payload = {
                    ...form,
                    externalDistributorEmail,
                    roleName,
                    roleId: roleId + '',
                    fundId: fundId + '',
                    stepId: tunnelStep.step.id,
                    fundName: currentInvestmentFund?.name,
                    label,
                    subcategoryId: subcategory,
                    referralEmail: queryEmail,
                    sgpName: currentInvestmentFund.sgp?.name ?? '',
                    sgpLogo: currentInvestmentFund.sgp?.logo?.url ?? '',
                };
                signUp({ data: payload, resubmit: false });
            }
        },
        [
            form,
            roleId,
            formIsValid,
            roleName,
            tunnelStep,
            signUp,
            fundId,
            label,
            subcategory,
            isResubmit,
            queryEmail,
            currentInvestmentFund,
            externalDistributorEmail,
        ]
    );

    useEffect(() => {
        if (isError) {
            if (error?.status === 409 && error?.data?.message === InternalErrors.USERNAME_EXISTS) {
                setEmailExists(true);
            }
        }
    }, [isError, error]);

    useEffect(() => {
        if (isSuccess && (isResubmit || userStep || hasFinishedSubscription)) {
            reset();
            handleNextStep(true);
        }
    }, [isSuccess, isResubmit, handleNextStep, userStep, hasFinishedSubscription, reset]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                handleSubmit,
                checkChanges,
                !!internalUser && !isCreatingSubscription
            ),
        [handleSubmit, checkChanges, internalUser, isCreatingSubscription, setTunnelChangesHandlers]
    );

    return (
        <React.Fragment>
            {(!isSuccess || isCreatingSubscription) && (
                <TunnelBox>
                    {!!Object.keys(tunnelErrors).length && (
                        <ErrorMessage gutterBottom message={texts.FORM_HAS_ERRORS} />
                    )}
                    <form onSubmit={e => e.preventDefault()}>
                        <BasicInfoForm
                            emailExists={emailExists}
                            classes={classes}
                            user={internalUser}
                            form={form}
                            errors={errors}
                            handleChange={handleChange}
                            tunnelErrors={tunnelErrors}
                        />
                        {!internalUser && (
                            <PasswordForm
                                handleChange={handleChange}
                                form={form}
                                user={internalUser}
                                errors={errors}
                                classes={classes}
                            />
                        )}
                        <ConditionsForm
                            conditions={form.conditions}
                            onChange={handleConditionsChange}
                        />
                    </form>
                    {errors['conditions,termsAgreement'] && (
                        <ErrorMessage
                            align="center"
                            gutterBottom
                            message={texts.VOUS_DEVEZ_ACCEPTER_LES_CONDITIONS}
                        />
                    )}
                    {errors['conditions,acceptedEmails'] && (
                        <ErrorMessage
                            align="center"
                            gutterBottom
                            message={texts.VOUS_DEVEZ_ACCEPTER_D_ETRE_CONTACTEE}
                        />
                    )}
                    {isSuccess && (
                        <Typography align="left" gutterBottom variant="body2">
                            {texts.YOUR_INFO_SAVED}
                        </Typography>
                    )}
                </TunnelBox>
            )}
            {isSuccess && !isCreatingSubscription && (
                <Typography align="center" variant="body2">
                    {texts.SHOULD_CLOSE_PAGE}
                </Typography>
            )}
            {!isSuccess && (
                <SubmitButton
                    text={submitButtonText}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                />
            )}
        </React.Fragment>
    );
};

PPForm.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
    tunnelStep: PropTypes.object,
    roleName: PropTypes.string,
};

export default PPForm;
